import { Plan } from '@/src/components/desktop/srp/fareFamily/types'
import type { Fare, FlightCard } from '@/src/types/flight'
import { getFormattedDate } from '../date'
import { isEmpty } from '../generalUtils'
import { getSplrtJourneyContextForRT, isRegularFareFamilyPlan } from '../srp/RT/getSplrtJourneyContext'
import { getCookie } from '../browser'
import { getFlowTypeBasedOnBrand } from './flowType'
import { DeviceType } from '@/src/types/deviceType'
import { isFlyin } from '../generalUtils/isFlyin'


// Helper function to build fare object from Plan
function buildFareFromPlan (
    plan: Plan,
    dfd: string
): {
    basePrice: number
    fareKey: string
    price: number
    fareType: string
    solutionId: string
    comboFBC: string
    fareClass: string
} {
    return {
        basePrice: plan.basePrice,
        fareKey: plan.fareKey,
        price: plan.unModifiedTotalPrice || plan.price, //for RT journey unModifiedTotalPrice is used , for OW journey unModifiedTotalPrice is undefined
        fareType: dfd === 'R' ? 'REFUNDABLE' : 'NON_REFUNDABLE',
        solutionId: plan.solutionId,
        comboFBC: plan.comboFbc,
        fareClass: 'RETAIL'
    }
}

// Helper function to build fare object from Fare
function buildFareFromFlight (
    fareObj: Fare,
    dfd: string,
    solutionId: string | undefined,
    fareClass: string
): {
    basePrice: number
    fareKey: string
    price: number
    fareType: string
    solutionId: string | undefined
    comboFBC: string
    fareClass: string
} {
    return {
        basePrice: fareObj.bp,
        fareKey: fareObj.fk,
        price: fareObj.pr,
        fareType: dfd === 'R' ? 'REFUNDABLE' : 'NON_REFUNDABLE',
        solutionId: solutionId,
        comboFBC: fareObj.fb,
        fareClass: fareClass
    }
}

export function buildItineraryPayload ({
    triggerSrpEvent,
    urlQueryObj,
    flight,
    jsons,
    utmCurrency = 'SAR',
    fareFamilyData,
    deviceType,
}: {
    triggerSrpEvent?: (event: string, data: Record<string, string | number>) => void
    urlQueryObj: Record<string, string>
    flight: FlightCard[]
    jsons: any
    utmCurrency: string
    fareFamilyData?: (Plan | null)[]
    deviceType: DeviceType
}) {
    const onwardFlight = flight[0]
    const returnFlight = flight[1]
    const isReturnTrip = !!returnFlight && !!urlQueryObj.return_date

    const onwardPlan = fareFamilyData?.[0] ?? null
    const returnPlan = fareFamilyData?.[1] ?? null

    /**
     * SPLRT context:
     * SPLRT is only used when both fare-family plans are "Regular"/missing and the SPLRT price is valid.
     * This is centralized to keep behavior aligned with `useFlightDetails`.
     */
    const splrtContext = isReturnTrip
        ? getSplrtJourneyContextForRT({
            onwardCard: onwardFlight,
            returnCard: returnFlight,
            onwardPlan,
            returnPlan
        })
        : { isSplrtJourney: false as const }

    const isSplrtRt = !!splrtContext.isSplrtJourney
    const fareClass = isSplrtRt ? 'SPECIAL_RT' : 'RETAIL'

    const onwardDfd = isSplrtRt ? splrtContext.onwardSplrtDfd : onwardFlight?.dfd
    const returnDfd = isSplrtRt ? splrtContext.returnSplrtDfd : returnFlight?.dfd

    const onwardSolutionId = isSplrtRt ? splrtContext.onwardSolutionId : onwardFlight?.priceBreakup?.fare?.solutionId
    const returnSolutionId = isSplrtRt ? splrtContext.returnSolutionId : returnFlight?.priceBreakup?.fare?.solutionId

    const onwardFareOBJ: Fare =
        isSplrtRt && !isEmpty(splrtContext.onwardSFare) ? (splrtContext.onwardSFare as Fare) : onwardFlight?.priceBreakup?.fare || {}
    const returnFareOBJ: Fare =
        isSplrtRt && !isEmpty(splrtContext.returnSFare) ? (splrtContext.returnSFare as Fare) : returnFlight?.priceBreakup?.fare || {}

    // Build onward fare
    let onwardFare
    if (!isReturnTrip && onwardPlan) {
        // One-way flight: use fare family data if available
        onwardFare = buildFareFromPlan(onwardPlan, onwardDfd || '')
    } else if (isReturnTrip && onwardPlan && !isRegularFareFamilyPlan(onwardPlan)) {
        // Return trip: use fare family data only if not regular
        onwardFare = buildFareFromPlan(onwardPlan, onwardDfd || '')
    } else {
        // Use SPLRT (if valid) or regular flight data
        onwardFare = buildFareFromFlight(onwardFareOBJ, onwardDfd || '', onwardSolutionId, fareClass)
    }

    // Build return fare
    let returnFare = null
    if (returnFlight) {
        if (returnPlan && !isRegularFareFamilyPlan(returnPlan)) {
            // Use fare family data
            returnFare = buildFareFromPlan(returnPlan, returnDfd || '')
        } else {
            // Use SPLRT (if valid) or regular flight data
            returnFare = buildFareFromFlight(returnFareOBJ, returnDfd || '', returnSolutionId, fareClass)
        }
    }

    // Set fareGroupDisplayName if fare family data is available
    const itineraryMeta: any = {
        domain: jsons?.domain,
        ...(jsons?.userInfo && { userId: jsons.userInfo.usrId }),
        externalApi: false,
        international: true,
        multiCity: false,
        sid: jsons?.psid || '',
        abFeatures: ['FARE_BENEFIT_INFO'],
        sourceType: isFlyin() ? 'B2C' : (deviceType === 'MOBILE' ? "MOBILE" : 'B2C'),
        utmCurrency: utmCurrency,
        convFeeRequired: false,
        couponCode: urlQueryObj.couponCode || '',
        trackingDetails: { selectedCardListId: [0] },
        dcFlow: true,
        flowType: getFlowTypeBasedOnBrand(),
        abtest: getCookie('abtest') || 'N/A',
    }

    // Add fareGroupDisplayName if we have fare family data
    if (!isReturnTrip && onwardPlan) {
        // One-way: use title if plan exists
        itineraryMeta.fareGroupDisplayName = onwardPlan.title
    } else if (isReturnTrip && onwardPlan && !isRegularFareFamilyPlan(onwardPlan)) {
        // Return trip: use onward plan title if not regular
        itineraryMeta.fareGroupDisplayName = onwardPlan.title
    } else if (isReturnTrip && returnFlight && returnPlan && !isRegularFareFamilyPlan(returnPlan)) {
        // Return trip: use return plan title if not regular
        itineraryMeta.fareGroupDisplayName = returnPlan.title
    }

    //add event for RT
    try {
        if (isReturnTrip) {
            triggerSrpEvent?.('Air_UI_Action', {
                action_name: 'RT-initiate-create-itinerary',
                action_type: 'Click',
                onward_fare_key: onwardFare?.fareKey,
                return_fare_key: returnFare?.fareKey || '',
                onward_fare_price: onwardFare?.price || 0,
                return_fare_price: returnFare?.price || 0,
                fare_class: fareClass,
                journey_type: 'Return',
                onward_fare_rule_type: isRegularFareFamilyPlan(onwardPlan) ? 'Regular' : onwardPlan?.ctBrandName || '',
                return_fare_rule_type: isRegularFareFamilyPlan(returnPlan) ? 'Regular' : returnPlan?.ctBrandName || ''
            })
        }
    } catch (error) {
        console.error('Error triggerSrpEvent: RT-initiate-create-itinerary', error)
    }
    return {
        flightParams: [
            {
                cabinType: urlQueryObj.class?.toUpperCase() || 'ECONOMY',
                departDate: urlQueryObj.depart_date ? getFormattedDate(urlQueryObj.depart_date, 'YYYY-MM-DD') : '',
                from: urlQueryObj.from || '',
                returnDate: urlQueryObj.return_date ? getFormattedDate(urlQueryObj.return_date, 'YYYY-MM-DD') : '',
                onwardFares: [onwardFare],
                returnFares: returnFare ? [returnFare] : [],
                to: urlQueryObj.to || ''
            }
        ],
        itineraryMeta,
        paxInfo: {
            adults: Number(urlQueryObj.adults || 1),
            children: Number(urlQueryObj.childs || 0),
            infants: Number(urlQueryObj.infants || 0)
        }
    }
}
